import { Errno, Filetype, WasiError, readFile, rmTree, writeFile, type Vfs } from "idfweb-vfs";

import type { FsCall, FsDirEntry, FsError, FsReply, FsRequest, FsResult, FsStat } from "./protocol.ts";

/**
 * The guest filesystem over a MessagePort: whole-file calls as messages.
 *
 * Neither half owns the port. The port between the workbench and the runtime
 * worker also carries build commands and build output, so each side of this
 * file is handed the messages that are its business and a way to send, and
 * whoever holds the port does the dispatch. That keeps this file free of the
 * DOM and the worker global, which is what lets it be tested under Node over
 * a MessageChannel.
 *
 * Array buffers are transferred when the data occupies the entire buffer, and
 * copied when referencing a view into a larger buffer: a transferred buffer
 * is gone from the sender, which may still hold the rest of it.
 */

/** The worker's half: answer one call against the filesystem it owns. */
export function answerFs(vfs: Vfs, call: FsCall): { result: FsResult; transfer: ArrayBuffer[] } {
    switch (call.op) {
        case "stat": {
            const stat = vfs.stat(call.path, true);
            const result: FsStat = {
                filetype: stat.filetype,
                size: stat.size,
                mtimeMs: stat.mtimeMs,
                ctimeMs: stat.ctimeMs,
            };
            return { result, transfer: [] };
        }
        case "readdir": {
            const result: FsDirEntry[] = vfs
                .readdir(call.path)
                .map((entry) => ({ name: entry.name, filetype: entry.filetype }));
            return { result, transfer: [] };
        }
        case "mkdir":
            vfs.mkdir(call.path);
            return { result: undefined, transfer: [] };
        case "readFile": {
            const bytes = readFile(vfs, call.path);
            return { result: bytes, transfer: [bytes.buffer] };
        }
        case "writeFile": {
            const there = exists(vfs, call.path);
            if (!there && !call.create) throw new WasiError(Errno.NOENT);
            if (there && !call.overwrite) throw new WasiError(Errno.EXIST);
            writeFile(vfs, call.path, call.data);
            return { result: undefined, transfer: [] };
        }
        case "delete":
            remove(vfs, call.path, call.recursive);
            return { result: undefined, transfer: [] };
        case "rename":
            vfs.rename(call.from, call.to);
            return { result: undefined, transfer: [] };
    }
}

/** `answerFs` as a reply message, with a refusal turned into an FsError. */
export function replyFs(vfs: Vfs, id: number, call: FsCall): { message: FsReply; transfer: ArrayBuffer[] } {
    try {
        const { result, transfer } = answerFs(vfs, call);
        return { message: { type: "fs", id, result }, transfer };
    } catch (e) {
        const error: FsError =
            e instanceof WasiError
                ? { errno: e.errno, message: e.message }
                : { message: e instanceof Error ? (e.stack ?? e.message) : String(e) };
        return { message: { type: "fs", id, error }, transfer: [] };
    }
}

/**
 * The client's half, used by the workbench and by the page: the same calls
 * as promises.
 *
 * Every message from the runtime goes through `receive`, which takes the
 * replies it is waiting for and leaves the rest to the caller. A refusal
 * with an errno comes back as the WasiError the guest would have thrown, so
 * the provider maps it the way it did when the filesystem was in-process.
 */
export class FsClient {
    readonly #post: (message: FsRequest, transfer: ArrayBuffer[]) => void;
    readonly #pending = new Map<number, { resolve: (result: FsResult) => void; reject: (reason: Error) => void }>();
    #nextId = 1;

    constructor(post: (message: FsRequest, transfer: ArrayBuffer[]) => void) {
        this.#post = post;
    }

    /** True if the message was a reply this client was waiting for. */
    receive(message: { type: string } | FsReply): boolean {
        if (!isFsReply(message)) return false;
        const waiting = this.#pending.get(message.id);
        if (waiting === undefined) return false;
        this.#pending.delete(message.id);
        if ("error" in message) {
            waiting.reject(
                message.error.errno !== undefined
                    ? new WasiError(message.error.errno as Errno)
                    : new Error(message.error.message),
            );
        } else {
            waiting.resolve(message.result);
        }
        return true;
    }

    /** Fail everything in flight: the other side is gone. */
    close(reason: string): void {
        for (const waiting of this.#pending.values()) waiting.reject(new Error(reason));
        this.#pending.clear();
    }

    stat(path: string): Promise<FsStat> {
        return this.#call({ op: "stat", path }) as Promise<FsStat>;
    }

    readdir(path: string): Promise<FsDirEntry[]> {
        return this.#call({ op: "readdir", path }) as Promise<FsDirEntry[]>;
    }

    async mkdir(path: string): Promise<void> {
        await this.#call({ op: "mkdir", path });
    }

    readFile(path: string): Promise<Uint8Array> {
        return this.#call({ op: "readFile", path }) as Promise<Uint8Array>;
    }

    async writeFile(path: string, data: Uint8Array, options: { create: boolean; overwrite: boolean }): Promise<void> {
        const whole = data.byteOffset === 0 && data.byteLength === data.buffer.byteLength;
        const bytes = whole ? data : data.slice();
        await this.#call({ op: "writeFile", path, data: bytes, create: options.create, overwrite: options.overwrite }, [
            bytes.buffer as ArrayBuffer,
        ]);
    }

    async delete(path: string, recursive: boolean): Promise<void> {
        await this.#call({ op: "delete", path, recursive });
    }

    async rename(from: string, to: string): Promise<void> {
        await this.#call({ op: "rename", from, to });
    }

    #call(call: FsCall, transfer: ArrayBuffer[] = []): Promise<FsResult> {
        const id = this.#nextId++;
        return new Promise((resolve, reject) => {
            this.#pending.set(id, { resolve, reject });
            this.#post({ type: "fs", id, call }, transfer);
        });
    }
}

function isFsReply(message: { type: string } | FsReply): message is FsReply {
    return message.type === "fs";
}

function exists(vfs: Vfs, path: string): boolean {
    try {
        vfs.stat(path, true);
        return true;
    } catch {
        return false;
    }
}

function remove(vfs: Vfs, path: string, recursive: boolean): void {
    if (recursive) rmTree(vfs, path);
    else if (vfs.stat(path, false).filetype === Filetype.DIRECTORY) vfs.rmdir(path);
    else vfs.unlink(path);
}
